import React, { useCallback, useRef } from 'react';
import { App, Button, Input, Modal, QRCode, Space } from 'antd';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';

import { PRINTER } from '../icons';
import { copyTextToClipboard } from '../../utils/clipboard';
import { addressUrl } from '../../utils/publicForms';
import { dataUrlToBlob } from '../../utils/dataUrlBlob';
import { anchorOf, print, saveFile } from '../../platform';

// ⚑ PAPER, not theme: a QR code is read by a camera, from a sign or a screen,
// and the one pair every scanner reads is dark modules on a light ground. The
// theme's ink (antd's default) is LIGHT in dark mode, which would draw a
// light code on a transparent ground — a downloaded PNG that prints as
// nothing. The same exception as `src/css/print.css`: paper is white in every
// theme (standards §0.1).
const PAPER_INK = 'black';
const PAPER = 'white';
// The quiet zone: the light margin every scanner needs round a code. antd
// draws none without its border, and does not pass `marginSize` through to
// the drawing library (5.28), so the margin is added here: paper round the
// code on screen, and painted round the PNG (independent review, KPR-513 —
// in dark mode the code sat straight on the dark dialog). About four modules.
const QUIET_FRACTION = 0.1;
const SCREEN_QUIET_PX = 20;
// The downloaded and printed code is drawn large, so a poster stays sharp.
const PRINT_SIZE = 1024;

const fileNameOf = name => `${`${name || 'public-form'}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'public-form'}-qr`;

/**
 * A sign for the address: the form's name, its QR code and the address under
 * it, printed from a frame of its own so the app around it never reaches the
 * paper. Built with DOM calls, never markup from strings: the name is a
 * manager's text.
 */
function printSign({ name, url, svg, anchor }) {
	if (typeof document === 'undefined' || !svg) return false;
	const frame = document.createElement('iframe');
	frame.setAttribute('aria-hidden', 'true');
	frame.setAttribute('tabindex', '-1');
	frame.style.position = 'fixed';
	frame.style.width = '0';
	frame.style.height = '0';
	frame.style.border = '0';
	document.body.appendChild(frame);
	const win = frame.contentWindow;
	const doc = frame.contentDocument || win?.document;
	if (!doc || !win) { frame.remove(); return false; }
	doc.title = name || 'Public form';
	const page = doc.createElement('main');
	page.style.cssText = 'font-family: system-ui, sans-serif; text-align: center; padding: 24mm 12mm;';
	const heading = doc.createElement('h1');
	heading.textContent = name || '';
	heading.style.cssText = 'font-size: 28pt; margin: 0 0 12mm;';
	const code = doc.importNode(svg, true);
	code.setAttribute('width', '120mm');
	code.setAttribute('height', '120mm');
	const address = doc.createElement('p');
	address.textContent = url;
	address.style.cssText = 'font-size: 16pt; margin: 10mm 0 0; word-break: break-all;';
	page.append(heading, code, address);
	doc.body.style.margin = '0';
	doc.body.appendChild(page);
	const cleanUp = () => { setTimeout(() => frame.remove(), 500); };
	win.addEventListener('afterprint', cleanUp, { once: true });
	// The platform prints the frame's window (focus first, on the web).
	print(win, { anchor }).catch(() => { frame.remove(); });
	// A browser that never fires afterprint still lets the frame go.
	setTimeout(() => { if (frame.isConnected) frame.remove(); }, 60000);
	return true;
}

/**
 * The address and its QR code (KPR-513; ruling f02: "a readable address that
 * is not secret … a QR code"). antd's QRCode draws it here in the browser —
 * nothing is sent anywhere to make it. Copy, Download QR (a PNG) and Print
 * (a sign: name, code, address).
 */
export default function PublicFormQrModal({ open, onClose, form }) {
	const { message } = App.useApp();
	const url = addressUrl(form?.address);
	const pngRef = useRef(null);
	const svgRef = useRef(null);
	const name = form?.name || 'Public form';

	const handleCopy = useCallback(async () => {
		if (!url) return;
		if (await copyTextToClipboard(url)) message.success('Address copied');
		else message.error('Could not copy the address');
	}, [url, message]);

	const handleDownload = useCallback(async (event) => {
		const anchor = anchorOf(event);
		const canvas = pngRef.current?.querySelector('canvas');
		if (!canvas || typeof document === 'undefined') { message.error('The QR code is not ready yet'); return; }
		const margin = Math.round(canvas.width * QUIET_FRACTION);
		const sheet = document.createElement('canvas');
		sheet.width = canvas.width + (2 * margin);
		sheet.height = canvas.height + (2 * margin);
		const pen = sheet.getContext('2d');
		pen.fillStyle = PAPER;
		pen.fillRect(0, 0, sheet.width, sheet.height);
		pen.drawImage(canvas, margin, margin);
		try {
			await saveFile(dataUrlToBlob(sheet.toDataURL('image/png')), `${fileNameOf(form?.name)}.png`, { anchor });
		} catch {
			message.error('Could not save the QR code');
		}
	}, [form, message]);

	const handlePrint = useCallback((event) => {
		const anchor = anchorOf(event);
		const svg = svgRef.current?.querySelector('svg');
		if (!printSign({ name, url, svg, anchor })) message.error('The QR code is not ready yet');
	}, [name, url, message]);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			title="Address and QR code"
			centered
			destroyOnClose
			className="share-modal public-form-qr"
		>
			<div className="modal-identity">
				<div className="micro-label">Public form</div>
				<div className="item-share-modal__preview-title">{name}</div>
			</div>

			<div className="item-share-modal__section">
				<div className="item-share-modal__section-title">Address</div>
				<Space.Compact block>
					<Input value={url} readOnly aria-label="Address" />
					<Button type="primary" icon={<CopyOutlined />} onClick={handleCopy} disabled={!url}>Copy</Button>
				</Space.Compact>
			</div>

			<div className="item-share-modal__section">
				<div className="item-share-modal__section-title">QR code</div>
				<div className="item-share-modal__qr public-form-qr__code">
					<div className="public-form-qr__paper" style={{ background: PAPER, padding: SCREEN_QUIET_PX }}>
						<QRCode value={url || ' '} bordered={false} size={176} color={PAPER_INK} bgColor={PAPER} errorLevel="M" />
					</div>
					<div className="public-form-qr__buttons">
						<Button icon={<DownloadOutlined />} onClick={handleDownload} disabled={!url}>Download QR</Button>
						<Button icon={<PRINTER />} onClick={handlePrint} disabled={!url}>Print</Button>
					</div>
				</div>
				<p className="share-links__hint">
					Anyone who scans it opens the form. Print it on a sign, a poster or a flyer — the address never expires.
				</p>
			</div>

			{/* What Download and Print draw from: large, off screen. */}
			<div className="public-form-qr__sources" aria-hidden="true">
				<div ref={pngRef}><QRCode value={url || ' '} bordered={false} size={PRINT_SIZE} color={PAPER_INK} bgColor={PAPER} errorLevel="M" /></div>
				<div ref={svgRef}><QRCode type="svg" value={url || ' '} bordered={false} size={PRINT_SIZE} color={PAPER_INK} bgColor={PAPER} errorLevel="M" /></div>
			</div>
		</Modal>
	);
}
